import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { useWS, useWSEvent } from "@/messaging/WSContext";
import type { Message, MessageList, UserBrief } from "@/messaging/types";

/** A message plus client-only optimistic state. */
export interface ChatMessage extends Message {
  /** Set while a send is in flight (temp bubble not yet on the server). */
  pending?: boolean;
  /** Set when a send failed and can be retried. */
  failed?: boolean;
  /** Stable client id for an optimistic bubble, used to reconcile with the
   *  server row (and to retry). Absent once reconciled. */
  localId?: string;
}

export interface SendInput {
  body?: string;
  attachmentUrl?: string;
  attachmentKind?: "image" | "gif" | "file";
  replyToId?: string | null;
}

export interface TypingUser {
  id: string;
  name: string;
  at: number;
}

type ThreadState = "loading" | "ready" | "error";

let _localSeq = 0;
function nextLocalId(): string {
  _localSeq += 1;
  return `local-${_localSeq}-${performance.now().toString(36)}`;
}

const PAGE = 40;

export function useMessages(conversationId: string | null) {
  const { user } = useAuth();
  const { send: wsSend } = useWS();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [state, setState] = useState<ThreadState>("loading");
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [typing, setTyping] = useState<TypingUser[]>([]);

  const nextBeforeRef = useRef<string | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const convRef = useRef(conversationId);
  convRef.current = conversationId;

  const me: UserBrief | null = user
    ? {
        id: user.id,
        full_name: user.full_name ?? null,
        email: user.email,
        avatar_url: user.avatar_url ?? null,
        presence_status: "active",
        is_online: true,
      }
    : null;
  const meRef = useRef(me);
  meRef.current = me;

  // ── initial load / conversation switch ──────────────────────────────────
  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      setState("ready");
      setHasMore(false);
      return;
    }
    let cancelled = false;
    setState("loading");
    setMessages([]);
    setTyping([]);
    (async () => {
      try {
        const r = await api<MessageList>(
          `/api/conversations/${conversationId}/messages?limit=${PAGE}`,
        );
        if (cancelled || convRef.current !== conversationId) return;
        // API returns newest→oldest; render oldest→newest.
        setMessages([...r.items].reverse());
        nextBeforeRef.current = r.next_before;
        setHasMore(!!r.next_before);
        setState("ready");
      } catch {
        if (!cancelled && convRef.current === conversationId) setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  const reload = useCallback(() => {
    // trigger the effect again by nudging state; simplest is to re-run the
    // fetch inline.
    const id = convRef.current;
    if (!id) return;
    setState("loading");
    (async () => {
      try {
        const r = await api<MessageList>(
          `/api/conversations/${id}/messages?limit=${PAGE}`,
        );
        if (convRef.current !== id) return;
        setMessages([...r.items].reverse());
        nextBeforeRef.current = r.next_before;
        setHasMore(!!r.next_before);
        setState("ready");
      } catch {
        if (convRef.current === id) setState("error");
      }
    })();
  }, []);

  const loadOlder = useCallback(async () => {
    const id = convRef.current;
    if (!id || !nextBeforeRef.current || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const before = encodeURIComponent(nextBeforeRef.current);
      const r = await api<MessageList>(
        `/api/conversations/${id}/messages?limit=${PAGE}&before=${before}`,
      );
      if (convRef.current !== id) return;
      const older = [...r.items].reverse();
      setMessages((prev) => [...older, ...prev]);
      nextBeforeRef.current = r.next_before;
      setHasMore(!!r.next_before);
    } catch {
      /* leave hasMore; the sentinel can retry on next scroll */
    } finally {
      setLoadingOlder(false);
    }
  }, [loadingOlder]);

  // ── send (optimistic) ───────────────────────────────────────────────────
  const doPost = useCallback(
    async (localId: string, id: string, input: SendInput) => {
      try {
        const saved = await api<Message>(
          `/api/conversations/${id}/messages`,
          {
            method: "POST",
            json: {
              body: input.body ?? null,
              attachment_url: input.attachmentUrl ?? null,
              attachment_kind: input.attachmentKind ?? null,
              reply_to_id: input.replyToId ?? null,
            },
          },
        );
        setMessages((prev) => {
          // If the WS echo already delivered the real row, just drop the temp.
          if (prev.some((m) => m.id === saved.id && m.localId !== localId)) {
            return prev.filter((m) => m.localId !== localId);
          }
          return prev.map((m) =>
            m.localId === localId
              ? { ...saved, localId: undefined, pending: false, failed: false }
              : m,
          );
        });
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.localId === localId ? { ...m, pending: false, failed: true } : m,
          ),
        );
      }
    },
    [],
  );

  const send = useCallback(
    (input: SendInput) => {
      const id = convRef.current;
      if (!id || !meRef.current) return;
      const localId = nextLocalId();
      const optimistic: ChatMessage = {
        id: localId,
        conversation_id: id,
        sender: meRef.current,
        body: input.body ?? null,
        attachment_url: input.attachmentUrl ?? null,
        attachment_kind: input.attachmentKind ?? null,
        edited_at: null,
        deleted_at: null,
        created_at: new Date().toISOString(),
        reply_to: null,
        reactions: [],
        pending: true,
        localId,
      };
      setMessages((prev) => [...prev, optimistic]);
      void doPost(localId, id, input);
    },
    [doPost],
  );

  const retry = useCallback(
    (localId: string) => {
      const id = convRef.current;
      const msg = messagesRef.current.find((m) => m.localId === localId);
      if (!id || !msg) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.localId === localId ? { ...m, pending: true, failed: false } : m,
        ),
      );
      void doPost(localId, id, {
        body: msg.body ?? undefined,
        attachmentUrl: msg.attachment_url ?? undefined,
        attachmentKind: (msg.attachment_kind as SendInput["attachmentKind"]) ?? undefined,
        replyToId: msg.reply_to?.id ?? null,
      });
    },
    [doPost],
  );

  const editMessage = useCallback(async (messageId: string, body: string) => {
    try {
      const saved = await api<Message>(`/api/messages/${messageId}`, {
        method: "PATCH",
        json: { body },
      });
      setMessages((prev) => prev.map((m) => (m.id === saved.id ? { ...m, ...saved } : m)));
    } catch {
      /* surfaced by the caller if needed */
    }
  }, []);

  const deleteMessage = useCallback(async (messageId: string) => {
    // optimistic soft-delete
    setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId
          ? { ...m, deleted_at: new Date().toISOString(), body: null, attachment_url: null }
          : m,
      ),
    );
    try {
      await api(`/api/messages/${messageId}`, { method: "DELETE" });
    } catch {
      /* WS will reconcile if the delete actually failed */
    }
  }, []);

  const toggleReaction = useCallback(async (messageId: string, emoji: string) => {
    try {
      const saved = await api<Message>(`/api/messages/${messageId}/reactions`, {
        method: "PUT",
        json: { emoji },
      });
      setMessages((prev) => prev.map((m) => (m.id === saved.id ? { ...m, ...saved } : m)));
    } catch {
      /* ignore */
    }
  }, []);

  const markRead = useCallback(() => {
    const id = convRef.current;
    if (!id) return;
    const list = messagesRef.current;
    const lastReal = [...list].reverse().find((m) => !m.localId);
    if (!lastReal) return;
    void api(`/api/conversations/${id}/read`, {
      method: "POST",
      json: { message_id: lastReal.id },
    }).catch(() => undefined);
  }, []);

  const lastTypingSentRef = useRef(0);
  const pingTyping = useCallback(() => {
    const id = convRef.current;
    if (!id) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 3000) return;
    lastTypingSentRef.current = now;
    wsSend({ type: "typing", conversation_id: id });
  }, [wsSend]);

  // ── live merge ──────────────────────────────────────────────────────────
  useWSEvent("message.new", (ev) => {
    if (ev.conversation_id !== convRef.current) return;
    setMessages((prev) => {
      if (prev.some((m) => m.id === ev.message.id)) return prev;
      // If this is my own echo and a pending temp exists, reconcile it.
      if (ev.message.sender?.id === meRef.current?.id) {
        const tempIdx = prev.findIndex(
          (m) => m.localId && m.body === ev.message.body && m.pending,
        );
        if (tempIdx >= 0) {
          const copy = [...prev];
          copy[tempIdx] = { ...ev.message };
          return copy;
        }
      }
      return [...prev, ev.message];
    });
  });

  useWSEvent("message.edited", (ev) => {
    if (ev.conversation_id !== convRef.current) return;
    setMessages((prev) => prev.map((m) => (m.id === ev.message.id ? { ...m, ...ev.message } : m)));
  });

  useWSEvent("message.deleted", (ev) => {
    if (ev.conversation_id !== convRef.current) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === ev.message_id
          ? { ...m, deleted_at: new Date().toISOString(), body: null, attachment_url: null }
          : m,
      ),
    );
  });

  useWSEvent("reaction.toggled", (ev) => {
    if (ev.conversation_id !== convRef.current) return;
    // Re-fetch nothing heavy — recompute the group locally.
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== ev.message_id) return m;
        const groups = [...(m.reactions ?? [])];
        const gi = groups.findIndex((g) => g.emoji === ev.emoji);
        const meId = meRef.current?.id;
        if (ev.removed) {
          if (gi >= 0) {
            const users = groups[gi].user_ids.filter((u) => u !== ev.user_id);
            if (users.length === 0) groups.splice(gi, 1);
            else
              groups[gi] = {
                ...groups[gi],
                user_ids: users,
                count: users.length,
                me: ev.user_id === meId ? false : groups[gi].me,
              };
          }
        } else if (gi >= 0) {
          if (!groups[gi].user_ids.includes(ev.user_id)) {
            const users = [...groups[gi].user_ids, ev.user_id];
            groups[gi] = {
              ...groups[gi],
              user_ids: users,
              count: users.length,
              me: ev.user_id === meId ? true : groups[gi].me,
            };
          }
        } else {
          groups.push({
            emoji: ev.emoji,
            count: 1,
            user_ids: [ev.user_id],
            me: ev.user_id === meId,
          });
        }
        return { ...m, reactions: groups };
      }),
    );
  });

  useWSEvent("typing", (ev) => {
    if (ev.conversation_id !== convRef.current) return;
    if (ev.user_id === meRef.current?.id) return;
    setTyping((prev) => {
      const rest = prev.filter((t) => t.id !== ev.user_id);
      return [...rest, { id: ev.user_id, name: ev.user_name, at: Date.now() }];
    });
  });

  // expire typing entries after 5s
  useEffect(() => {
    const iv = window.setInterval(() => {
      setTyping((prev) => {
        const cutoff = Date.now() - 5000;
        const next = prev.filter((t) => t.at > cutoff);
        return next.length === prev.length ? prev : next;
      });
    }, 1500);
    return () => window.clearInterval(iv);
  }, []);

  return {
    messages,
    state,
    hasMore,
    loadingOlder,
    loadOlder,
    reload,
    send,
    retry,
    editMessage,
    deleteMessage,
    toggleReaction,
    markRead,
    typing,
    pingTyping,
  };
}
